/**
 * Right-side dApp wallet drawer — Trust Wallet / mobile-wallet feel.
 *
 * Layers (never collapse):
 * - Play GBUX = D1 edge ledger (games spend)
 * - Chain GBUX / SOL = third-party inject or Phantom embed (mainnet)
 * - Crossmint custodial = wallet.grudge-studio.com (fleet)
 * - Fleet bag = Railway (JWT)
 *
 * SSOT: docs/WEB3_BALANCE_SESSION_SSOT.md · CROSSMINT_POKER.md · PHANTOM_EMBEDDED_SSOT.md
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useWalletState, type WalletPanelTab } from "@/contexts/wallet-context";
import { useToast } from "@/hooks/use-toast";
import { formatPlayGbux } from "@shared/gbux-math";
import { GBUX, FLEET, CROSSMINT_FLEET } from "@shared/fleet";
import {
  transferAndCreditPlay,
  creditPlayFromTx,
  fetchOnChainGbux,
  fetchOnChainSol,
  explorerTxUrl,
  PLAY_DEPOSIT_SSOT,
} from "@/lib/gbux-transfer";
import {
  isLoggedIn,
  loginWithGrudgeId,
  openGrudgeWalletSite,
} from "@/lib/grudge-auth";
import { exchangeQuote, exchangeSwap } from "@/lib/grudge-wallet-api";
import type { SolanaWalletId } from "@/lib/solana-wallets";
import { installUrl, preferredWalletId } from "@/lib/solana-wallets";
import { BRAND } from "@shared/brand";
import {
  Wallet,
  Loader2,
  Copy,
  ExternalLink,
  ArrowDownToLine,
  RefreshCw,
  LogOut,
  User,
  ShieldCheck,
  ArrowLeftRight,
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  Image as ImageIcon,
  Sparkles,
  Bot,
  Link2,
} from "lucide-react";

const BUDBai = PLAY_DEPOSIT_SSOT.agent;
const RATE = GBUX.rateUsd; // $0.001 / GBUX

function normalizeTab(t: WalletPanelTab): Exclude<WalletPanelTab, "deposit"> {
  if (t === "deposit") return "transfer";
  return t;
}

function shortAddr(a: string) {
  if (!a || a.length < 12) return a || "—";
  return `${a.slice(0, 4)}…${a.slice(-4)}`;
}

function usdFromGbux(n: number) {
  return (Number(n) || 0) * RATE;
}

function formatUsd(n: number) {
  if (!Number.isFinite(n) || n <= 0) return "$0.00";
  if (n < 0.01) return `$${n.toFixed(4)}`;
  return `$${n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function WalletAccountDrawer() {
  const {
    wallet,
    walletPanelOpen,
    walletPanelTab,
    openWalletPanel,
    closeWalletPanel,
    connect,
    disconnect,
    refreshBalances,
    refreshMultiWalletBalances,
    selectWallet,
    forgetWallet,
    getConnectedWallet,
    ensureSigner,
    canSign,
    detectedWallets,
    rescanWallets,
    loginGrudgeId,
  } = useWalletState();
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const qc = useQueryClient();

  const [tab, setTab] = useState<Exclude<WalletPanelTab, "deposit">>("overview");
  const [amount, setAmount] = useState("");
  const [txSig, setTxSig] = useState("");
  const [busy, setBusy] = useState(false);
  const [agentAddr, setAgentAddr] = useState(BUDBai);
  const [liveChain, setLiveChain] = useState<number | null>(null);
  const [liveSol, setLiveSol] = useState<number | null>(null);
  const [lastTx, setLastTx] = useState<string | null>(null);
  const [step, setStep] = useState<
    "idle" | "signing" | "confirming" | "crediting" | "done"
  >("idle");
  const [signingReady, setSigningReady] = useState(false);
  const [walletMenuOpen, setWalletMenuOpen] = useState(false);

  const [swapDir, setSwapDir] = useState<"sol-to-gbux" | "gbux-to-sol">(
    "sol-to-gbux",
  );
  const [swapAmt, setSwapAmt] = useState("0.1");
  const [swapQuote, setSwapQuote] = useState<Record<string, unknown> | null>(
    null,
  );

  const multi = wallet.multiWallets || [];

  const hydrateLive = useCallback(async () => {
    const pk = wallet.publicKey || getConnectedWallet()?.publicKey;
    if (!pk || pk.length < 32) {
      setLiveChain(null);
      setLiveSol(null);
      return;
    }
    const [g, s] = await Promise.all([
      fetchOnChainGbux(pk),
      fetchOnChainSol(pk),
    ]);
    setLiveChain(g);
    setLiveSol(s);
  }, [wallet.publicKey, getConnectedWallet]);

  useEffect(() => {
    if (walletPanelOpen) setTab(normalizeTab(walletPanelTab));
  }, [walletPanelOpen, walletPanelTab]);

  useEffect(() => {
    if (!walletPanelOpen) return;
    void fetch("/api/agent/budbai")
      .then((r) => r.json())
      .then((d) => {
        if (d?.address) setAgentAddr(d.address);
      })
      .catch(() => {});
    void (async () => {
      await refreshBalances();
      await refreshMultiWalletBalances();
      await hydrateLive();
      if (!getConnectedWallet()) {
        try {
          const c = await ensureSigner({ silentOnly: true });
          setSigningReady(Boolean(c));
        } catch {
          setSigningReady(false);
        }
      } else {
        setSigningReady(true);
      }
    })();
  }, [
    walletPanelOpen,
    refreshBalances,
    refreshMultiWalletBalances,
    hydrateLive,
    ensureSigner,
    getConnectedWallet,
  ]);

  useEffect(() => {
    setSigningReady(canSign || Boolean(getConnectedWallet()));
  }, [canSign, getConnectedWallet, wallet.publicKey]);

  useEffect(() => {
    if (!walletPanelOpen) return;
    if (amount) return;
    const c = liveChain != null ? liveChain : Number(wallet.chainGbux || 0);
    if (c >= 1) setAmount(String(Math.floor(c)));
  }, [walletPanelOpen, liveChain, wallet.chainGbux, amount]);

  const play = Number(wallet.playGbux || wallet.gbuxBalance || 0);
  const chain = liveChain != null ? liveChain : Number(wallet.chainGbux || 0);
  const sol =
    liveSol != null
      ? liveSol
      : Number(wallet.chainSol || wallet.solBalance || 0);
  const fleetBag = Number(wallet.accountGbux || 0);
  const custodial = wallet.custodialWallet;
  const custodialGbux = Number(wallet.custodialChainGbux || 0);
  const pk = wallet.publicKey || getConnectedWallet()?.publicKey || null;
  const canTransfer = Boolean(getConnectedWallet());

  const portfolioUsd = useMemo(
    () =>
      usdFromGbux(play) +
      usdFromGbux(chain) +
      usdFromGbux(fleetBag) +
      usdFromGbux(custodialGbux),
    [play, chain, fleetBag, custodialGbux],
  );

  const afterCredit = async (
    playGbux: number,
    credited: number,
    sig?: string,
  ) => {
    toast({
      title: "Play GBUX funded",
      description: `+${formatPlayGbux(credited)} → play ${formatPlayGbux(playGbux)}`,
    });
    setTxSig("");
    setStep("done");
    if (sig) setLastTx(sig);
    await refreshBalances();
    await refreshMultiWalletBalances();
    await hydrateLive();
    void qc.invalidateQueries({ queryKey: ["wallet-scopes"] });
    void qc.invalidateQueries({ queryKey: ["poker-profile"] });
    if (pk) {
      const left = await fetchOnChainGbux(pk);
      if (left >= 1) setAmount(String(Math.floor(left)));
      else setAmount("");
    }
  };

  const onSelectMulti = async (address: string) => {
    setWalletMenuOpen(false);
    setBusy(true);
    try {
      await selectWallet(address);
      await ensureSigner({ silentOnly: true });
      await hydrateLive();
      toast({
        title: "Active wallet",
        description: shortAddr(address),
      });
    } catch (e) {
      toast({
        title: "Switch failed",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const onReconnectSign = async () => {
    setBusy(true);
    try {
      let c = await ensureSigner({ silentOnly: false });
      if (!c) {
        await connect("phantom", { phantomMode: "injected" });
        c = getConnectedWallet();
      }
      if (!c) throw new Error("Wallet did not connect");
      setSigningReady(true);
      await refreshBalances();
      await hydrateLive();
      toast({
        title: "Ready to sign",
        description: "Mainnet session bound for SPL transfers",
      });
      setTab("transfer");
    } catch (e) {
      toast({
        title: "Connect failed",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
      setTab("connect");
    } finally {
      setBusy(false);
    }
  };

  const onTransferFromWallet = async () => {
    const amt = Math.floor(parseFloat(amount) || 0);
    if (amt < 1) {
      toast({ title: "Enter GBUX amount (≥ 1)", variant: "destructive" });
      return;
    }
    if (chain > 0 && amt > chain + 0.0001) {
      toast({
        title: "Not enough on-chain GBUX",
        description: `Wallet has ${formatPlayGbux(chain)} GBUX`,
        variant: "destructive",
      });
      return;
    }

    setBusy(true);
    setStep("signing");
    try {
      let connected = getConnectedWallet();
      if (!connected) {
        connected = await ensureSigner({ silentOnly: false });
      }
      if (!connected) {
        toast({
          title: "Reconnect wallet to sign",
          description: "Phantom / inject session required for Transfer",
          variant: "destructive",
        });
        setTab("connect");
        setStep("idle");
        return;
      }
      toast({
        title: "Approve in wallet",
        description: `Approve ${amt} GBUX → play (1:1 · mainnet)`,
      });
      setStep("confirming");
      const result = await transferAndCreditPlay({
        wallet: connected,
        amountHuman: amt,
        depositTo: agentAddr,
      });
      setStep("crediting");
      await afterCredit(result.playGbux, result.gbuxCredited, result.signature);
      setTab("overview");
    } catch (e) {
      setStep("idle");
      toast({
        title: "Transfer failed",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const onPasteVerify = async () => {
    const addr = pk;
    if (!addr || addr.length < 32) {
      toast({ title: "Connect a wallet first", variant: "destructive" });
      return;
    }
    if (!txSig || txSig.length < 40) {
      toast({ title: "Paste a Solana tx signature", variant: "destructive" });
      return;
    }
    setBusy(true);
    setStep("crediting");
    try {
      const credit = await creditPlayFromTx(addr, txSig.trim());
      await afterCredit(credit.playGbux, credit.gbuxCredited, txSig.trim());
      setTab("overview");
    } catch (e) {
      setStep("idle");
      toast({
        title: "Verify failed",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const onConnect = async (id: SolanaWalletId) => {
    setBusy(true);
    try {
      if (id === "phantom") {
        await connect("phantom", { phantomMode: "injected" });
      } else {
        await connect(id);
      }
      setSigningReady(true);
      await refreshBalances();
      await refreshMultiWalletBalances();
      await hydrateLive();
      toast({ title: "Wallet connected", description: "Mainnet · ready to fund play" });
      setTab("transfer");
      const connected = getConnectedWallet();
      const key = connected?.publicKey;
      if (key) {
        const g = await fetchOnChainGbux(key);
        if (g >= 1) setAmount(String(Math.floor(g)));
      }
    } catch (e) {
      toast({
        title: "Connect failed",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const setPctOfChain = (pct: number) => {
    const n = Math.floor(chain * pct);
    if (n >= 1) setAmount(String(n));
    else if (chain > 0) setAmount(String(Math.max(1, Math.floor(chain))));
  };

  const onFetchSwapQuote = async () => {
    if (!isLoggedIn()) {
      toast({
        title: "Sign in with Grudge ID",
        description: "Fleet bag swaps need JWT",
        variant: "destructive",
      });
      return;
    }
    const n = parseFloat(swapAmt);
    if (!Number.isFinite(n) || n <= 0) {
      toast({ title: "Enter a positive amount", variant: "destructive" });
      return;
    }
    setBusy(true);
    try {
      setSwapQuote(await exchangeQuote(n, swapDir));
    } catch (e) {
      setSwapQuote(null);
      toast({
        title: "Quote failed",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const onRunSwap = async () => {
    if (!isLoggedIn()) {
      toast({ title: "Sign in with Grudge ID", variant: "destructive" });
      return;
    }
    const n = parseFloat(swapAmt);
    if (!Number.isFinite(n) || n <= 0) return;
    setBusy(true);
    try {
      const res = await exchangeSwap(n, swapDir);
      toast({
        title: "Fleet swap submitted",
        description:
          typeof res?.message === "string" ? res.message : "Check fleet bag",
      });
      await refreshBalances();
      setSwapQuote(null);
    } catch (e) {
      toast({
        title: "Swap failed",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet
      open={walletPanelOpen}
      onOpenChange={(o) => (o ? openWalletPanel(tab) : closeWalletPanel())}
    >
      <SheetContent
        side="right"
        className="wallet-dapp-sheet w-full sm:max-w-md border-zinc-800 bg-zinc-950 p-0 text-zinc-100 overflow-y-auto max-sm:max-h-[92dvh] max-sm:rounded-t-2xl"
      >
        <div className="wallet-dapp-sheet__hero relative overflow-hidden px-5 pt-5 pb-4">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_-20%,rgba(245,158,11,0.22),transparent_55%)]" />
          <SheetHeader className="relative z-10 space-y-1 text-left">
            <div className="flex items-center justify-between gap-2">
              <SheetTitle className="flex items-center gap-2 text-lg text-amber-200">
                <Wallet className="h-5 w-5" />
                Wallet
              </SheetTitle>
              <span className="rounded-full border border-emerald-500/40 bg-emerald-950/50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-300">
                Solana mainnet
              </span>
            </div>
            <SheetDescription className="text-xs leading-relaxed text-zinc-400">
              <strong className="text-emerald-300">Play GBUX</strong> ={" "}
              <strong className="text-zinc-200">real GBUX</strong> in play on
              this dApp (tables · slots · BJ). Fund from Phantom/other (1:1) or
              buy @ <strong className="text-amber-200">${RATE}</strong>/GBUX
              via agent · Crossmint on Grudge Wallet.
            </SheetDescription>
          </SheetHeader>

          {/* Portfolio total */}
          <div className="relative z-10 mt-4">
            <div className="text-[11px] uppercase tracking-wider text-zinc-500">
              Est. GBUX value
            </div>
            <div className="text-3xl font-black tabular-nums tracking-tight text-white">
              {formatUsd(portfolioUsd)}
            </div>
            <div className="mt-0.5 text-[11px] text-zinc-500">
              @ ${RATE.toFixed(3)} USD · not including SOL spot
            </div>
          </div>

          {/* Active account picker */}
          <div className="relative z-20 mt-4">
            <button
              type="button"
              className="flex w-full items-center gap-3 rounded-2xl border border-white/10 bg-black/40 px-3 py-2.5 text-left backdrop-blur-sm transition hover:border-amber-500/35"
              onClick={() => setWalletMenuOpen((v) => !v)}
            >
              <div
                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border ${
                  canTransfer
                    ? "border-emerald-400/50 bg-emerald-950/60"
                    : "border-amber-400/40 bg-amber-950/40"
                }`}
              >
                {canTransfer ? (
                  <CheckCircle2 className="h-5 w-5 text-emerald-300" />
                ) : (
                  <Wallet className="h-5 w-5 text-amber-200" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-zinc-100">
                  {pk
                    ? `${wallet.walletType || "Wallet"} · ${shortAddr(pk)}`
                    : "No wallet selected"}
                </div>
                <div className="text-[11px] text-zinc-500">
                  {canTransfer
                    ? "Signing ready · Transfer opens wallet approval"
                    : pk
                      ? "Reconnect to sign SPL"
                      : "Connect Phantom, Solflare, …"}
                </div>
              </div>
              <ChevronDown
                className={`h-4 w-4 shrink-0 text-zinc-400 transition ${
                  walletMenuOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            {walletMenuOpen && (
              <div className="absolute left-0 right-0 top-[calc(100%+6px)] z-30 max-h-56 overflow-y-auto rounded-xl border border-zinc-700 bg-zinc-900 shadow-2xl">
                {multi.length === 0 && !pk && (
                  <div className="px-3 py-3 text-xs text-zinc-500">
                    No registered wallets — use Connect tab.
                  </div>
                )}
                {multi.map((w) => {
                  const active = w.address === pk;
                  return (
                    <button
                      key={w.address}
                      type="button"
                      className={`flex w-full items-center gap-2 border-b border-zinc-800 px-3 py-2.5 text-left text-xs last:border-0 ${
                        active ? "bg-amber-950/30" : "hover:bg-zinc-800/80"
                      }`}
                      onClick={() => void onSelectMulti(w.address)}
                    >
                      <span className="font-semibold text-zinc-200">
                        {w.label || w.walletId}
                      </span>
                      <span className="font-mono text-zinc-500">
                        {shortAddr(w.address)}
                      </span>
                      <span className="ml-auto tabular-nums text-cyan-300/90">
                        {formatPlayGbux(Number(w.chainGbux || 0), {
                          compact: true,
                        })}
                      </span>
                      {active && (
                        <span className="text-[10px] font-bold text-emerald-400">
                          ACTIVE
                        </span>
                      )}
                    </button>
                  );
                })}
                {pk && !multi.some((w) => w.address === pk) && (
                  <div className="border-b border-zinc-800 px-3 py-2 font-mono text-xs text-zinc-400">
                    {shortAddr(pk)} (current)
                  </div>
                )}
                <button
                  type="button"
                  className="w-full px-3 py-2 text-left text-xs text-violet-300 hover:bg-zinc-800"
                  onClick={() => {
                    setWalletMenuOpen(false);
                    setTab("connect");
                  }}
                >
                  + Connect another wallet
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-3 px-5 pb-8">
          {/* Asset cards — Trust-style list */}
          <div className="space-y-2">
            <AssetRow
              icon={BRAND.gbuxCoinSm}
              title="Play GBUX"
              subtitle="Real GBUX · dApp spend (tables · slots · BJ)"
              amount={formatPlayGbux(play)}
              usd={formatUsd(usdFromGbux(play))}
              accent="emerald"
            />
            <AssetRow
              icon={BRAND.gbuxCoinSm}
              title="Wallet GBUX"
              subtitle={`On-chain SPL · ${GBUX.network} · transfer → play 1:1`}
              amount={formatPlayGbux(chain)}
              usd={formatUsd(usdFromGbux(chain))}
              accent="cyan"
            />
            <AssetRow
              icon={null}
              title="SOL"
              subtitle="Gas · buy path"
              amount={sol.toFixed(4)}
              usd="—"
              accent="violet"
              leading={<span className="text-lg">◎</span>}
            />
            {(fleetBag > 0 || isLoggedIn()) && (
              <AssetRow
                icon={BRAND.gbuxCoinSm}
                title="Fleet bag"
                subtitle="Railway · Grudge ID"
                amount={formatPlayGbux(fleetBag)}
                usd={formatUsd(usdFromGbux(fleetBag))}
                accent="violet"
              />
            )}
          </div>

          {/* Crossmint / custodial */}
          <div className="rounded-2xl border border-violet-500/25 bg-gradient-to-br from-violet-950/40 to-black/40 p-3">
            <div className="mb-1 flex items-center gap-2 text-xs font-bold text-violet-200">
              <Sparkles className="h-3.5 w-3.5" />
              Studio bag
            </div>
            <p className="text-[11px] leading-relaxed text-zinc-400">
              Railway fleet bag at{" "}
              <strong className="text-zinc-300">wallet.grudge-studio.com</strong>
              . Poker sits from the play ledger. Phantom signs. Crossmint stays
              on the server for mint/ops — not this connect flow.
            </p>
            {custodial ? (
              <div className="mt-2 flex items-center justify-between rounded-lg bg-black/30 px-2 py-1.5 text-[11px]">
                <span className="font-mono text-zinc-400">
                  {shortAddr(custodial)}
                </span>
                <span className="tabular-nums text-violet-200">
                  {formatPlayGbux(custodialGbux)} GBUX
                </span>
              </div>
            ) : (
              <p className="mt-1 text-[11px] text-zinc-500">
                No custodial address linked — open Grudge Wallet after Grudge ID
                login.
              </p>
            )}
            <div className="mt-2 flex flex-wrap gap-2">
              <Button
                size="sm"
                className="h-8 bg-violet-700 text-xs hover:bg-violet-600"
                onClick={() => openGrudgeWalletSite()}
              >
                Open dashboard
                <ExternalLink className="ml-1 h-3 w-3" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-8 border-zinc-700 text-xs"
                onClick={() =>
                  window.open(CROSSMINT_FLEET.walletSite, "_blank", "noopener")
                }
              >
                Create / fund wallet
              </Button>
            </div>
          </div>

          {/* NFT / cNFT / agent */}
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-3 text-left transition hover:border-zinc-600"
              onClick={() =>
                toast({
                  title: "NFT / cNFT",
                  description:
                    "Compressed NFTs & collections are managed on Grudge Wallet / fleet — not play chips.",
                })
              }
            >
              <ImageIcon className="mb-1 h-4 w-4 text-zinc-400" />
              <div className="text-xs font-semibold text-zinc-200">NFT / cNFT</div>
              <div className="text-[10px] text-zinc-500">
                Crossmint collections · fleet
              </div>
            </button>
            <button
              type="button"
              className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-3 text-left transition hover:border-zinc-600"
              onClick={() => {
                void navigator.clipboard.writeText(agentAddr);
                toast({
                  title: "BUDBai deposit address",
                  description: shortAddr(agentAddr),
                });
              }}
            >
              <Bot className="mb-1 h-4 w-4 text-amber-300" />
              <div className="text-xs font-semibold text-zinc-200">BUDBai</div>
              <div className="text-[10px] text-zinc-500">
                Sales / rake agent · {shortAddr(agentAddr)}
              </div>
            </button>
          </div>

          {pk && !canTransfer && (
            <div className="flex items-start gap-2 rounded-xl border border-amber-500/35 bg-amber-950/30 px-3 py-2 text-xs text-amber-100">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div className="flex-1">
                Address saved but not signing-ready.
                <Button
                  size="sm"
                  className="mt-2 h-8 bg-amber-600 hover:bg-amber-500"
                  disabled={busy}
                  onClick={() => void onReconnectSign()}
                >
                  Reconnect to sign
                </Button>
              </div>
            </div>
          )}

          {step !== "idle" && step !== "done" && (
            <div className="relative min-h-[72px] overflow-hidden rounded-xl border border-amber-500/35">
              <img
                src={BRAND.transferLoad}
                alt=""
                className="absolute inset-0 h-full w-full object-cover opacity-40"
                draggable={false}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/70 to-black/40" />
              <div className="relative z-10 flex items-center gap-2 px-3 py-3 text-[12px] font-medium text-amber-50">
                <Loader2 className="h-4 w-4 shrink-0 animate-spin text-amber-300" />
                <span>
                  {step === "signing" && "Approve in wallet…"}
                  {step === "confirming" && "Confirming Solana mainnet…"}
                  {step === "crediting" && "Crediting play ledger…"}
                </span>
              </div>
            </div>
          )}

          <Tabs
            value={tab}
            onValueChange={(v) => setTab(normalizeTab(v as WalletPanelTab))}
            className="w-full"
          >
            <TabsList className="grid h-auto w-full grid-cols-4 bg-zinc-900">
              <TabsTrigger value="overview" className="px-1 text-[11px]">
                Home
              </TabsTrigger>
              <TabsTrigger value="transfer" className="px-1 text-[11px]">
                Transfer
              </TabsTrigger>
              <TabsTrigger value="swap" className="px-1 text-[11px]">
                Swap
              </TabsTrigger>
              <TabsTrigger value="connect" className="px-1 text-[11px]">
                Connect
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-3 space-y-2">
              <p className="text-xs leading-relaxed text-zinc-500">
                <strong className="text-emerald-300">Play GBUX is real</strong>{" "}
                product money for this dApp — not fake chips. Games debit play
                only. Sources: third-party wallet transfer (1:1), purchase @ $
                {RATE}/GBUX (SOL/USDT via AI agent), or house/AI agent systems.
              </p>
              <Button
                className="w-full bg-amber-600 font-semibold hover:bg-amber-500"
                onClick={() =>
                  setTab(canTransfer || pk ? "transfer" : "connect")
                }
              >
                <ArrowDownToLine className="mr-2 h-4 w-4" />
                Fund play wallet
              </Button>
              <Button
                variant="outline"
                className="w-full border-zinc-700"
                onClick={() => {
                  void refreshBalances()
                    .then(() => refreshMultiWalletBalances())
                    .then(() => hydrateLive());
                  toast({ title: "Balances refreshed" });
                }}
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Refresh all wallets
              </Button>
              <Button
                variant="outline"
                className="w-full border-zinc-700"
                onClick={() => openGrudgeWalletSite()}
              >
                <Link2 className="mr-2 h-4 w-4" />
                Grudge Wallet (Crossmint)
                <ExternalLink className="ml-auto h-3.5 w-3.5 opacity-60" />
              </Button>
              <Button
                variant="outline"
                className="w-full border-zinc-700"
                onClick={() => {
                  closeWalletPanel();
                  setLocation("/account");
                }}
              >
                <User className="mr-2 h-4 w-4" />
                Full account
              </Button>
              {!isLoggedIn() && (
                <Button
                  variant="secondary"
                  className="w-full"
                  onClick={() => loginGrudgeId("/lobby")}
                >
                  Sign in Grudge ID
                </Button>
              )}
              {wallet.connected && (
                <Button
                  variant="ghost"
                  className="w-full text-red-400"
                  onClick={() => void disconnect()}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Disconnect active
                </Button>
              )}
              {pk && multi.length > 1 && (
                <Button
                  variant="ghost"
                  className="w-full text-zinc-500 text-xs"
                  onClick={() => {
                    forgetWallet(pk);
                    toast({ title: "Removed from registry", description: shortAddr(pk) });
                  }}
                >
                  Forget this address (local)
                </Button>
              )}
              {lastTx && (
                <a
                  href={explorerTxUrl(lastTx)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-[11px] text-cyan-400"
                >
                  Last deposit on Solscan
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </TabsContent>

            <TabsContent value="transfer" className="mt-3 space-y-3">
              <div className="space-y-2 rounded-xl border border-emerald-500/25 bg-emerald-500/5 p-3 text-xs">
                <div className="flex items-center gap-1 font-semibold text-emerald-400">
                  <ShieldCheck className="h-3.5 w-3.5" />
                  Chain GBUX → play (1:1 · mainnet)
                </div>
                <p className="text-[11px] text-zinc-400">
                  Select transfer-from wallet above, then approve SPL to BUDBai.
                  Edge verifies signature before crediting play.
                </p>
                {/* From wallet select (same multi list) */}
                <label className="block text-[10px] uppercase tracking-wide text-zinc-500">
                  Transfer from
                </label>
                <select
                  className="w-full rounded-lg border border-zinc-700 bg-zinc-900 px-2 py-2 text-xs text-zinc-100"
                  value={pk || ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v) void onSelectMulti(v);
                  }}
                >
                  {!pk && <option value="">Select wallet…</option>}
                  {pk && !multi.some((w) => w.address === pk) && (
                    <option value={pk}>{shortAddr(pk)} (current)</option>
                  )}
                  {multi.map((w) => (
                    <option key={w.address} value={w.address}>
                      {(w.label || w.walletId) +
                        " · " +
                        shortAddr(w.address) +
                        (w.chainGbux != null
                          ? ` · ${formatPlayGbux(Number(w.chainGbux), { compact: true })} GBUX`
                          : "")}
                    </option>
                  ))}
                </select>
                <div className="pt-1">
                  <span className="text-[10px] uppercase text-zinc-500">
                    Deposit agent
                  </span>
                  <div className="mt-0.5 font-mono text-[11px] text-zinc-400">
                    {shortAddr(agentAddr)}
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-0 text-cyan-400"
                    onClick={() => {
                      void navigator.clipboard.writeText(agentAddr);
                      toast({ title: "Deposit address copied" });
                    }}
                  >
                    Copy full address
                  </Button>
                </div>
              </div>

              {!canTransfer && (
                <div className="rounded-lg border border-amber-500/30 bg-amber-950/20 p-3 text-xs text-amber-100/90">
                  No signing session for this address.
                  <Button
                    size="sm"
                    className="mt-2 w-full bg-amber-600 hover:bg-amber-500"
                    disabled={busy}
                    onClick={() => void onReconnectSign()}
                  >
                    Reconnect to sign
                  </Button>
                </div>
              )}

              <div>
                <div className="mb-1 flex justify-between text-xs text-zinc-400">
                  <span>Amount (on-chain GBUX)</span>
                  <span className="tabular-nums text-cyan-300/90">
                    available {formatPlayGbux(chain)} ·{" "}
                    {formatUsd(usdFromGbux(chain))}
                  </span>
                </div>
                <Input
                  type="number"
                  min={1}
                  step={1}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder={chain >= 1 ? String(Math.floor(chain)) : "0"}
                  className="border-zinc-700 bg-zinc-900"
                  disabled={busy}
                />
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {[10, 25, 50, 100, 250, 500, 1000].map((n) => (
                    <button
                      key={n}
                      type="button"
                      className="rounded-md border border-zinc-700 bg-zinc-800 px-2 py-1 text-[11px] hover:border-amber-500/50 disabled:opacity-40"
                      disabled={chain < n}
                      onClick={() => setAmount(String(n))}
                    >
                      {n}
                    </button>
                  ))}
                  {chain >= 1 && (
                    <>
                      <button
                        type="button"
                        className="rounded-md border border-cyan-600/40 bg-cyan-900/40 px-2 py-1 text-[11px]"
                        onClick={() => setPctOfChain(0.25)}
                      >
                        25%
                      </button>
                      <button
                        type="button"
                        className="rounded-md border border-cyan-600/40 bg-cyan-900/40 px-2 py-1 text-[11px]"
                        onClick={() => setPctOfChain(0.5)}
                      >
                        50%
                      </button>
                      <button
                        type="button"
                        className="rounded-md border border-amber-600/40 bg-amber-900/40 px-2 py-1 text-[11px] font-bold"
                        onClick={() => setPctOfChain(1)}
                      >
                        Max
                      </button>
                    </>
                  )}
                </div>
              </div>

              <Button
                className="h-12 w-full bg-amber-600 text-base font-bold hover:bg-amber-500"
                disabled={busy || !canTransfer || chain < 1}
                onClick={() => void onTransferFromWallet()}
              >
                {busy ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <ArrowDownToLine className="mr-2 h-4 w-4" />
                )}
                Sign &amp; send GBUX → play
              </Button>
              {chain < 1 && pk && (
                <p className="text-center text-[11px] text-zinc-500">
                  No GBUX on this wallet. Buy / receive SPL, then Transfer.
                  Sales list price ${RATE}/GBUX.
                </p>
              )}

              <div className="relative py-2">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-zinc-800" />
                </div>
                <div className="relative flex justify-center text-[10px] uppercase">
                  <span className="bg-zinc-950 px-2 text-zinc-600">
                    or verify existing tx
                  </span>
                </div>
              </div>

              <Input
                value={txSig}
                onChange={(e) => setTxSig(e.target.value)}
                placeholder="Solana signature if you already sent GBUX"
                className="border-zinc-700 bg-zinc-900 font-mono text-xs"
                disabled={busy}
              />
              <Button
                variant="outline"
                className="w-full border-zinc-700"
                disabled={busy || !pk}
                onClick={() => void onPasteVerify()}
              >
                Verify signature → credit play
              </Button>
            </TabsContent>

            <TabsContent value="swap" className="mt-3 space-y-3">
              <div className="space-y-2 rounded-lg border border-amber-500/25 bg-amber-950/20 p-3 text-xs text-zinc-400">
                <div className="flex items-center gap-1.5 font-semibold text-amber-200">
                  <ArrowLeftRight className="h-3.5 w-3.5" />
                  Play cash-out → email
                </div>
                <p>
                  Off-app play cash-out is staff-only:{" "}
                  <a
                    className="text-amber-300 underline"
                    href={`mailto:${FLEET.supportEmail}?subject=BUDB%20cash-out`}
                  >
                    {FLEET.supportEmail}
                  </a>
                </p>
              </div>
              <div className="space-y-2 rounded-lg border border-violet-500/25 bg-violet-950/20 p-3 text-xs text-zinc-400">
                <div className="flex items-center gap-1.5 font-semibold text-violet-200">
                  Fleet bag SOL ↔ GBUX
                </div>
                <p>
                  Railway bag only (JWT). After bag GBUX is on-chain, use
                  Transfer to fund play. Rate ${RATE}/GBUX.
                </p>
                {!isLoggedIn() && (
                  <Button
                    size="sm"
                    className="w-full bg-violet-700 hover:bg-violet-600"
                    onClick={() => loginWithGrudgeId("/lobby")}
                  >
                    Sign in Grudge ID
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={swapDir === "sol-to-gbux" ? "default" : "outline"}
                  className={
                    swapDir === "sol-to-gbux"
                      ? "flex-1 bg-amber-600"
                      : "flex-1 border-zinc-700"
                  }
                  onClick={() => {
                    setSwapDir("sol-to-gbux");
                    setSwapQuote(null);
                  }}
                >
                  SOL → GBUX
                </Button>
                <Button
                  size="sm"
                  variant={swapDir === "gbux-to-sol" ? "default" : "outline"}
                  className={
                    swapDir === "gbux-to-sol"
                      ? "flex-1 bg-amber-600"
                      : "flex-1 border-zinc-700"
                  }
                  onClick={() => {
                    setSwapDir("gbux-to-sol");
                    setSwapQuote(null);
                  }}
                >
                  GBUX → SOL
                </Button>
              </div>
              <Input
                type="number"
                min={0}
                step="any"
                value={swapAmt}
                onChange={(e) => setSwapAmt(e.target.value)}
                className="border-zinc-700 bg-zinc-900"
                disabled={busy}
              />
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1 border-zinc-700"
                  disabled={busy || !isLoggedIn()}
                  onClick={() => void onFetchSwapQuote()}
                >
                  Quote
                </Button>
                <Button
                  className="flex-1 bg-violet-700 hover:bg-violet-600"
                  disabled={busy || !isLoggedIn()}
                  onClick={() => void onRunSwap()}
                >
                  Swap
                </Button>
              </div>
              {swapQuote && (
                <pre className="max-h-32 overflow-x-auto rounded-lg border border-zinc-800 bg-black/40 p-2 text-[10px] text-zinc-400">
                  {JSON.stringify(swapQuote, null, 2)}
                </pre>
              )}
            </TabsContent>

            <TabsContent value="connect" className="mt-3 space-y-2">
              <p className="mb-2 text-xs text-zinc-500">
                Phantom Browser SDK (inject / Google / Apple embed) or other
                inject wallets. Opt-in only — no auto-connect. Mainnet only.
              </p>
              <div className="mb-2 grid grid-cols-3 gap-1.5">
                <Button
                  size="sm"
                  className="h-9 bg-violet-700 text-[11px] hover:bg-violet-600"
                  disabled={busy}
                  onClick={() => void onConnect("phantom")}
                >
                  Extension
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-9 border-zinc-700 text-[11px]"
                  disabled={busy}
                  onClick={async () => {
                    setBusy(true);
                    try {
                      await connect("phantom", { phantomMode: "google" });
                      await refreshBalances();
                      await hydrateLive();
                      setTab("transfer");
                    } catch (e) {
                      toast({
                        title: "Phantom Google failed",
                        description:
                          e instanceof Error ? e.message : String(e),
                        variant: "destructive",
                      });
                    } finally {
                      setBusy(false);
                    }
                  }}
                >
                  Google
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-9 border-zinc-700 text-[11px]"
                  disabled={busy}
                  onClick={async () => {
                    setBusy(true);
                    try {
                      await connect("phantom", { phantomMode: "apple" });
                      await refreshBalances();
                      await hydrateLive();
                      setTab("transfer");
                    } catch (e) {
                      toast({
                        title: "Phantom Apple failed",
                        description:
                          e instanceof Error ? e.message : String(e),
                        variant: "destructive",
                      });
                    } finally {
                      setBusy(false);
                    }
                  }}
                >
                  Apple
                </Button>
              </div>
              <Button
                className="w-full bg-violet-700 font-semibold hover:bg-violet-600"
                disabled={busy}
                onClick={() => {
                  const id = preferredWalletId(detectedWallets) || "phantom";
                  void onConnect(id);
                }}
              >
                {busy ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                Connect preferred wallet
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="mb-1 text-zinc-400"
                onClick={() => rescanWallets()}
              >
                <RefreshCw className="mr-1 h-3.5 w-3.5" /> Rescan extensions
              </Button>
              {detectedWallets.map((w) => (
                <Button
                  key={w.id}
                  variant={w.available ? "secondary" : "outline"}
                  className="w-full justify-start border-zinc-700"
                  disabled={busy || (!w.available && w.id === "generic")}
                  onClick={() =>
                    w.available
                      ? void onConnect(w.id)
                      : window.open(installUrl(w.id), "_blank")
                  }
                >
                  <span className="mr-2">{w.icon}</span>
                  {w.name}
                  {!w.available && (
                    <span className="ml-auto text-[10px] text-zinc-500">
                      Install
                    </span>
                  )}
                </Button>
              ))}
              <Button
                variant="outline"
                className="w-full border-violet-500/40 text-violet-200"
                onClick={() => {
                  window.location.href = "/connect";
                }}
              >
                No extension? Phantom Google / Apple
              </Button>
            </TabsContent>
          </Tabs>

          <p className="mt-2 text-center text-[10px] text-zinc-600">
            mainnet-beta · mint {GBUX.mint.slice(0, 8)}… · $
            {RATE.toFixed(3)}/GBUX · sources{" "}
            {(wallet.balanceSources || []).join(", ") || "—"}
            {signingReady ? " · sign-ready" : ""}
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function AssetRow({
  icon,
  title,
  subtitle,
  amount,
  usd,
  accent,
  leading,
}: {
  icon: string | null;
  title: string;
  subtitle: string;
  amount: string;
  usd: string;
  accent: "emerald" | "cyan" | "violet";
  leading?: ReactNode;
}) {
  const border =
    accent === "emerald"
      ? "border-emerald-500/25 bg-emerald-500/5"
      : accent === "cyan"
        ? "border-cyan-500/25 bg-cyan-500/5"
        : "border-violet-500/25 bg-violet-500/5";
  const amt =
    accent === "emerald"
      ? "text-emerald-300"
      : accent === "cyan"
        ? "text-cyan-200"
        : "text-violet-200";
  return (
    <div
      className={`flex items-center gap-3 rounded-2xl border px-3 py-2.5 ${border}`}
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-black/40">
        {leading
          ? leading
          : icon && (
              <img
                src={icon}
                alt=""
                className="h-7 w-7 object-contain"
                draggable={false}
              />
            )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-zinc-100">{title}</div>
        <div className="truncate text-[10px] text-zinc-500">{subtitle}</div>
      </div>
      <div className="text-right">
        <div className={`text-sm font-bold tabular-nums ${amt}`}>{amount}</div>
        <div className="text-[10px] tabular-nums text-zinc-500">{usd}</div>
      </div>
    </div>
  );
}

/** Floating play + chain chip (Trust-style glance) */
export function WalletPlayChip() {
  const { wallet, openWalletPanel, canSign, getConnectedWallet } =
    useWalletState();
  const play = wallet.playGbux || wallet.gbuxBalance;
  const chain = wallet.chainGbux || 0;
  const ready = canSign || Boolean(getConnectedWallet());

  return (
    <button
      type="button"
      onClick={() =>
        openWalletPanel(
          wallet.publicKey
            ? ready
              ? "transfer"
              : "overview"
            : "connect",
        )
      }
      className="wallet-play-chip"
      style={{
        position: "fixed",
        top: "max(10px, env(safe-area-inset-top))",
        right: 12,
        zIndex: 45,
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "8px 12px",
        borderRadius: 999,
        border: ready
          ? "1px solid rgba(52,211,153,0.45)"
          : "1px solid rgba(232,199,106,0.35)",
        background: "rgba(8,10,20,0.9)",
        backdropFilter: "blur(10px)",
        color: "#fff8e7",
        cursor: "pointer",
        boxShadow: "0 4px 20px rgba(0,0,0,0.45)",
        fontSize: 13,
        fontWeight: 700,
      }}
      aria-label="Open wallet and play GBUX"
    >
      <img
        src={BRAND.gbuxCoinSm}
        alt="GBUX"
        width={18}
        height={18}
        style={{ width: 18, height: 18, objectFit: "contain" }}
        draggable={false}
      />
      <span className="tabular-nums text-emerald-300">
        {formatPlayGbux(play, { compact: true })}
      </span>
      <span className="text-[10px] font-semibold tracking-wide text-white/45">
        PLAY
      </span>
      {chain > 0 && (
        <>
          <span className="text-white/25">|</span>
          <span className="tabular-nums text-[11px] text-cyan-300/90">
            {formatPlayGbux(chain, { compact: true })}
          </span>
          <span className="text-[9px] text-white/40">CHAIN</span>
        </>
      )}
    </button>
  );
}
